import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api, apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import EventCard from '../../../components/events/EventCard'
import MemberGroupsSection from '../../../components/groups/MemberGroupsSection'
import AnnouncementsFeed from '../../../components/announcements/AnnouncementsFeed'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowLeft,
  Loader2,
  ExternalLink,
  Star,
  Heart,
  DollarSign,
  Repeat,
  CheckCircle,
  XCircle,
  Calendar,
  CreditCard,
  Users,
  ArrowRight,
} from 'lucide-react'

export default function ParishDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [parish, setParish] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAdded, setIsAdded] = useState(false)
  const [isPrimary, setIsPrimary] = useState(false)
  const [addingParish, setAddingParish] = useState(false)
  const [userParishes, setUserParishes] = useState([])
  const [myDonations, setMyDonations] = useState([])
  const [donationsLoading, setDonationsLoading] = useState(false)
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(false)

  const fetchUpcomingEvents = async () => {
    if (!id) return
    try {
      setEventsLoading(true)
      const response = await apiRequest(
        `/api/v1/parishes/${id}/events?isPublished=true&page=1&limit=3`,
      )
      setUpcomingEvents(response.data || [])
    } catch (err) {
      console.error('Failed to fetch events:', err)
    } finally {
      setEventsLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchParish()
      fetchUserParishes()
      fetchMyDonations()
      fetchUpcomingEvents()
    }
  }, [id, user?.id])

  const fetchParish = async () => {
    try {
      setLoading(true)
      setError(null)
      const parishData = await api.getParish(id)
      setParish(parishData)
    } catch (err) {
      console.error('Failed to fetch parish:', err)
      setError(err.message || 'Failed to load parish')
    } finally {
      setLoading(false)
    }
  }

  const fetchUserParishes = async () => {
    try {
      if (user?.id) {
        const userData = await api.getUser()
        const parishes = userData.parishes || []
        setUserParishes(parishes)
        
        const parishMatch = parishes.find(p => p.parishId === id)
        if (parishMatch) {
          setIsAdded(true)
          setIsPrimary(parishMatch.isPrimary || false)
        }
      }
    } catch (err) {
      console.error('Failed to fetch user parishes:', err)
    }
  }

  const handleAddParish = async (makePrimary = false) => {
    if (!user?.id) return

    try {
      setAddingParish(true)
      setError(null)

      await api.updateUserParish(user.id, id, makePrimary)
      await fetchUserParishes()
      setIsAdded(true)
      setIsPrimary(makePrimary)
    } catch (err) {
      console.error('Failed to add parish:', err)
      setError(err.message || 'Failed to add parish')
    } finally {
      setAddingParish(false)
    }
  }

  const handleSetPrimary = async () => {
    await handleAddParish(true)
  }

  const fetchMyDonations = async () => {
    if (!user?.id) return

    try {
      setDonationsLoading(true)
      const response = await api.getUserDonations(user.id, { page: 1, limit: 50 })
      // Filter donations for this specific parish
      const parishDonations = (response.data || []).filter(
        donation => donation.parishId === id
      )
      setMyDonations(parishDonations)
    } catch (err) {
      console.error('Failed to fetch donations:', err)
    } finally {
      setDonationsLoading(false)
    }
  }


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'text-green-400'
      case 'failed':
        return 'text-red-600'
      case 'pending':
      case 'processing':
        return 'text-yellow-400'
      default:
        return 'text-charcoal-muted'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="w-4 h-4" />
      case 'failed':
        return <XCircle className="w-4 h-4" />
      default:
        return null
    }
  }

  const handleOneTimeDonation = () => {
    navigate(`/app/donor/donations?donate=${id}`)
  }

  const handleRecurringDonation = () => {
    navigate(`/app/donor/recurring-donation-setup?parishId=${id}`)
  }

  const formatAddress = () => {
    if (!parish) return null
    const parts = [parish.address, parish.city, parish.state, parish.zipCode].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }

  const formatMassTimes = () => {
    if (!parish?.parishInfo?.massTimes) return null

    const massTimes = parish.parishInfo.massTimes

    // Handle array format
    if (Array.isArray(massTimes)) {
      if (massTimes.length === 0) return null
      // New grouped format: Array of { day, times: [{ time, note? }] }
      if (typeof massTimes[0] === 'object' && massTimes[0].day && Array.isArray(massTimes[0].times)) {
        return massTimes.map((dayGroup, index) => (
          <div key={index} className="py-2">
            <div className="font-semibold text-charcoal mb-1">{dayGroup.day}</div>
            <div className="space-y-0.5 pl-3">
              {dayGroup.times.map((slot, si) => (
                <div key={si} className="text-base text-charcoal/90 flex items-center gap-2">
                  <span>{slot.time}</span>
                  {slot.note && <span className="text-sm text-charcoal-muted">({slot.note})</span>}
                </div>
              ))}
            </div>
          </div>
        ))
      }
      // Legacy flat format: Array of { day, time } objects
      if (typeof massTimes[0] === 'object' && massTimes[0].day) {
        // Group by day for display
        const grouped = {}
        massTimes.forEach(entry => {
          if (!grouped[entry.day]) grouped[entry.day] = []
          grouped[entry.day].push(entry.time)
        })
        return Object.entries(grouped).map(([day, times]) => (
          <div key={day} className="py-2">
            <div className="font-semibold text-charcoal mb-1">{day}</div>
            <div className="space-y-0.5 pl-3">
              {times.map((time, i) => (
                <div key={i} className="text-base text-charcoal/90">{time}</div>
              ))}
            </div>
          </div>
        ))
      }
      // Array of strings (legacy)
      return massTimes.map((time, index) => (
        <div key={index} className="text-base text-charcoal/90 py-1">
          {typeof time === 'string' ? time : JSON.stringify(time)}
        </div>
      ))
    }

    // Handle object format (e.g., { "Sunday": "9:00 AM", "Monday": "7:00 AM" })
    if (typeof massTimes === 'object') {
      return Object.entries(massTimes).map(([day, time]) => (
        <div key={day} className="text-base text-charcoal/90 py-1 flex justify-between items-center">
          <span className="font-semibold">{day}:</span>
          <span>{time}</span>
        </div>
      ))
    }

    // Handle string format
    if (typeof massTimes === 'string') {
      return (
        <div className="text-base text-charcoal/90 py-1">
          {massTimes}
        </div>
      )
    }

    return null
  }

  const getGoogleMapsUrl = () => {
    if (!parish) return null
    const address = formatAddress()
    if (!address) return null
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  }

  const formatConfessionTimes = () => {
    const times = parish?.parishInfo?.confessionTimes
    if (!times || !Array.isArray(times) || times.length === 0) return null

    // New grouped format: Array of { day, times: [{ startTime, endTime, note? }] }
    if (typeof times[0] === 'object' && times[0].day && Array.isArray(times[0].times)) {
      return times.map((dayGroup, index) => (
        <div key={index} className="py-2">
          <div className="font-semibold text-charcoal mb-1">{dayGroup.day}</div>
          <div className="space-y-0.5 pl-3">
            {dayGroup.times.map((slot, si) => (
              <div key={si} className="text-base text-charcoal/90 flex items-center gap-2">
                <span>{slot.startTime} &ndash; {slot.endTime}</span>
                {slot.note && <span className="text-sm text-charcoal-muted">({slot.note})</span>}
              </div>
            ))}
          </div>
        </div>
      ))
    }

    // Legacy flat format: Array of { day, time }
    return times.map((entry, index) => (
      <div key={index} className="text-base text-charcoal/90 py-1 flex justify-between items-center">
        <span className="font-semibold">{entry.day}:</span>
        <span>{entry.time}</span>
      </div>
    ))
  }

  const formatParishStaff = () => {
    const staff = parish?.parishInfo?.parishStaff
    if (!staff || !Array.isArray(staff) || staff.length === 0) return null
    return staff
  }

  const massTimesContent = formatMassTimes()
  const hasMassTimes = massTimesContent !== null
  const confessionTimesContent = formatConfessionTimes()
  const hasConfessionTimes = confessionTimesContent !== null
  const staffData = formatParishStaff()
  const hasStaff = staffData !== null
  const address = formatAddress()
  const googleMapsUrl = getGoogleMapsUrl()

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-charcoal hover:text-charcoal-muted mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold font-display text-charcoal">Parish Details</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : error && !parish ? (
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      ) : !parish ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <p className="text-charcoal-muted mb-4">Parish not found</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      ) : (
        <>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Cover Image */}
      {parish.imageUrl && (
        <div className="relative w-full h-64 md:h-96 rounded overflow-hidden">
          <img
            src={parish.imageUrl}
            alt={parish.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-background-dark/50 to-transparent" />
          
          {/* Action Buttons Over Image */}
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            {isPrimary && (
              <div className="flex items-center gap-2 bg-gold/15 backdrop-blur-sm px-3 py-2 rounded-full">
                <Star className="w-4 h-4 text-charcoal fill-gold" />
                <span className="text-sm text-charcoal font-semibold">Primary</span>
              </div>
            )}
            {!isAdded && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAddParish(userParishes.length === 0)}
                disabled={addingParish}
                className="bg-white/90 backdrop-blur-sm text-charcoal border-gold/30"
              >
                {addingParish ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Heart className="w-4 h-4 mr-1" />
                    Add Parish
                  </>
                )}
              </Button>
            )}
            {isAdded && !isPrimary && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSetPrimary}
                disabled={addingParish}
                className="bg-white/90 backdrop-blur-sm text-charcoal border-gold/30"
              >
                {addingParish ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-1" />
                    Set as Primary
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Name and Brief Bio */}
          <div className="bg-white border border-surface-border rounded p-6">
            <h2 className="text-2xl font-bold font-display text-charcoal mb-3">{parish.name}</h2>
            {parish.briefBio && (
              <p className="text-lg text-charcoal/90 leading-relaxed mb-4">
                {parish.briefBio}
              </p>
            )}
            {parish.description && (
              <div className="mt-4 pt-4 border-t border-surface-border">
                <p className="text-base text-charcoal-muted leading-relaxed whitespace-pre-wrap">
                  {parish.description}
                </p>
              </div>
            )}
          </div>

          {/* Unified announcements feed: parish, event, and group announcements merged */}
          <AnnouncementsFeed parishId={id} />

          {/* Private groups the viewer belongs to — renders nothing if they're in none */}
          <MemberGroupsSection parishId={id} />

          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <div className="bg-white border border-surface-border rounded p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gold" />
                  <h3 className="text-xl font-semibold font-display text-charcoal">Upcoming Events</h3>
                </div>
                <Link
                  to={`/app/donor/parish/${id}/events`}
                  className="text-sm text-gold hover:text-gold/80 font-medium flex items-center gap-1"
                >
                  View All Events
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {eventsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 text-gold animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {upcomingEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onClick={() => navigate(`/app/donor/parish/${id}/events/${event.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-white border border-surface-border rounded p-6">
            <h3 className="text-xl font-semibold font-display text-charcoal mb-4">Contact Information</h3>
            <div className="space-y-4">
              {address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-charcoal-muted mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-charcoal/90">{address}</p>
                    {googleMapsUrl && (
                      <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-charcoal-muted hover:text-charcoal mt-1 inline-flex items-center gap-1"
                      >
                        View on Google Maps
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              )}
              {parish.phoneNumber && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-charcoal-muted flex-shrink-0" />
                  <a
                    href={`tel:${parish.phoneNumber}`}
                    className="text-charcoal/90 hover:text-charcoal"
                  >
                    {parish.phoneNumber}
                  </a>
                </div>
              )}
              {parish.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-charcoal-muted flex-shrink-0" />
                  <a
                    href={`mailto:${parish.email}`}
                    className="text-charcoal/90 hover:text-charcoal break-all"
                  >
                    {parish.email}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Mass Times */}
          {hasMassTimes && (
            <div className="bg-white border border-surface-border rounded p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-charcoal-muted" />
                <h3 className="text-xl font-semibold font-display text-charcoal">Mass Times</h3>
              </div>
              <div className="space-y-1">
                {massTimesContent}
              </div>
            </div>
          )}

          {/* Confession Times */}
          {hasConfessionTimes && (
            <div className="bg-white border border-surface-border rounded p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-charcoal-muted" />
                <h3 className="text-xl font-semibold font-display text-charcoal">Confession Times</h3>
              </div>
              <div className="space-y-1">
                {confessionTimesContent}
              </div>
            </div>
          )}

          {/* Parish Staff */}
          {hasStaff && (
            <div className="bg-white border border-surface-border rounded p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-charcoal-muted" />
                <h3 className="text-xl font-semibold font-display text-charcoal">Parish Staff</h3>
              </div>
              <div className="space-y-4">
                {staffData.map((member, index) => (
                  <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-surface-border last:border-b-0">
                    <div>
                      <span className="text-sm text-charcoal-muted">{member.title}</span>
                      <p className="text-base font-semibold text-charcoal">{member.name}</p>
                    </div>
                    <div className="flex items-center gap-4 mt-1 sm:mt-0">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="flex items-center gap-1 text-sm text-charcoal-muted hover:text-charcoal">
                          <Mail className="w-3.5 h-3.5" />
                          {member.email}
                        </a>
                      )}
                      {member.phone && (
                        <a href={`tel:${member.phone}`} className="flex items-center gap-1 text-sm text-charcoal-muted hover:text-charcoal">
                          <Phone className="w-3.5 h-3.5" />
                          {member.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* My Donations to This Parish */}
          <div className="bg-white border border-surface-border rounded p-6">
            <h3 className="text-xl font-semibold font-display text-charcoal mb-4">My Donations</h3>
            {donationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-gold animate-spin" />
              </div>
            ) : myDonations.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
                <p className="text-charcoal-muted text-sm">
                  You haven't made any donations to this parish yet.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {myDonations.map((donation) => (
                  <div
                    key={donation.id}
                    className="bg-white border border-surface-border rounded p-4 hover:border-gold/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-charcoal">
                          {formatCurrency(donation.amount)}
                        </span>
                        <span className={`text-xs font-medium flex items-center gap-1 ${getStatusColor(donation.status)}`}>
                          {getStatusIcon(donation.status)}
                          {donation.status.charAt(0).toUpperCase() + donation.status.slice(1)}
                        </span>
                      </div>
                      {donation.donationType && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          donation.donationType === 'recurring'
                            ? 'bg-gold/15 text-charcoal'
                            : donation.donationType === 'roundup'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gold/10 text-charcoal-muted'
                        }`}>
                          {donation.donationType === 'recurring' ? 'Recurring' : donation.donationType === 'roundup' ? 'Round-Up' : 'One-Time'}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-charcoal-muted">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(donation.completedAt || donation.createdAt)}</span>
                      </div>
                      {donation.paymentMethodLast4 && (
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          <span>•••• {donation.paymentMethodLast4}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Actions */}
        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="bg-white border border-surface-border rounded p-6 space-y-3">
            {!isAdded && (
              <Button
                onClick={() => handleAddParish(userParishes.length === 0)}
                disabled={addingParish}
                className="w-full bg-gold text-black hover:bg-gold/90"
              >
                {addingParish ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Heart className="w-4 h-4 mr-2" />
                )}
                {userParishes.length === 0 ? 'Add as Primary Parish' : 'Add Parish'}
              </Button>
            )}
            {isAdded && !isPrimary && (
              <Button
                onClick={handleSetPrimary}
                disabled={addingParish}
                variant="outline"
                className="w-full text-charcoal border-gold/50 hover:border-gold"
              >
                {addingParish ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Star className="w-4 h-4 mr-2" />
                )}
                Set as Primary
              </Button>
            )}
            {isPrimary && (
              <div className="flex items-center gap-2 bg-gold/15 px-4 py-3 rounded">
                <Star className="w-4 h-4 text-charcoal fill-gold" />
                <span className="text-sm text-charcoal font-semibold">Primary Parish</span>
              </div>
            )}
            <Button
              onClick={handleOneTimeDonation}
              className="w-full bg-gold text-black hover:bg-gold/90"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Make One-Time Donation
            </Button>
            <Button
              onClick={handleRecurringDonation}
              className="w-full bg-gold text-black hover:bg-gold/90"
            >
              <Repeat className="w-4 h-4 mr-2" />
              Make Recurring Donation
            </Button>
          </div>

          {/* Additional Info */}
          {(() => {
            const additionalInfoEntries = parish.parishInfo
              ? Object.entries(parish.parishInfo).filter(([key, value]) => {
                  if (['massTimes', 'confessionTimes', 'parishStaff'].includes(key)) return false
                  if (value === null || value === undefined || value === '') return false
                  if (Array.isArray(value) && value.length === 0) return false
                  if (typeof value === 'object' && Object.keys(value).length === 0) return false
                  return true
                })
              : []
            if (additionalInfoEntries.length === 0) return null
            return (
              <div className="bg-white border border-surface-border rounded p-6">
                <h3 className="text-lg font-semibold font-display text-charcoal mb-4">Additional Information</h3>
                <div className="space-y-2 text-sm text-charcoal-muted">
                  {additionalInfoEntries.map(([key, value]) => (
                    <div key={key}>
                      <span className="font-semibold capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>{' '}
                      <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })()}
        </div>
      </div>
        </>
      )}
    </div>
  )
}
